import { ASSIGNED_SEATING } from "@shared/constants";
import Konva from "konva";
import SeatingService from "../services/SeatingService";
import {
  PublicBaseLocationSerializer,
  TransformType,
  CurvedRowDisplay,
} from "../types/seating-types";
import SeatFactory from "./SeatFactory";
import SeatingUtilsFactory from "./SeatingUtilsFactory";
import { calculateCurvedSeatPosition } from "../utils/curvedRowUtils";

type CreateLocationParamsType = {
  location: PublicBaseLocationSerializer;
  transform: TransformType;
  seatSize: number;
  seatSpacing: number;
  segmentTransform: TransformType;
  hideLocationNames: boolean;
  name?: string;
};

export default class RowFactory {
  public seatingService: SeatingService;

  public seatFactory: SeatFactory;

  constructor(seatingService: SeatingService, seatFactory: SeatFactory) {
    this.seatingService = seatingService;
    this.seatFactory = seatFactory;
    this.createSquareTable = this.createSquareTable.bind(this);
    this.createRoundTable = this.createRoundTable.bind(this);
    this.createSemiRoundTable = this.createSemiRoundTable.bind(this);
    this.create = this.create.bind(this);
  }

  static createLabel = (
    text: string,
    {
      x = 0,
      y = 0,
      width,
      height,
      fill = "#555555",
      hidden = false,
      fontSize = 32,
    }: Konva.TextConfig
  ): Konva.Text => {
    const label = new Konva.Text({
      offsetX: width && width / 2,
      offsetY: height && height / 2,
      x,
      y,
      text: text.toUpperCase(),
      width,
      height,
      fontSize,
      align: "center",
      verticalAlign: "middle",
      fill,
      listening: false,
    });

    if (hidden) {
      label.hide();
    }

    return label;
  };

  createRoundTable({
    location,
    transform,
    seatSize,
    segmentTransform,
    hideLocationNames,
    name,
  }: CreateLocationParamsType): {
    groupActive: Konva.Group;
    groupDisabled: Konva.Group;
  } {
    const { x, y, rotation } = transform;

    const groupActive = new Konva.Group({
      x,
      y,
      name: `${name}_active`,
    });

    const groupDisabled = new Konva.Group({
      x,
      y,
      name: `${name}_disabled`,
    });

    const display = new Konva.Circle({
      x: 0,
      y: 0,
      radius: seatSize * 2.17,
      fill: ASSIGNED_SEATING.SEMI_CIRCLE_TABLE.STYLE.colorFill,
      stroke: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.colorStroke,
      strokeWidth: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.strokeWidth,
      listening: false,
      name: "display",
    });
    const label = RowFactory.createLabel(location.identifier, {
      width: display.width(),
      height: display.height(),
      fill: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.labelColor,
      fontSize: seatSize * 1.35,
    });

    groupDisabled.add(display);
    if (!hideLocationNames) {
      groupDisabled.add(label);
    }

    location.seats.forEach((seat, index) => {
      const tableRadius = seatSize * 2.17;
      const radius = tableRadius + Number(seatSize) + seatSize / 2; // Radius of the curve around table
      const angle = (2 * Math.PI * index) / location.capacity; // angle in radians

      const seatElement = this.seatFactory.create(
        seat,
        {
          x: Math.sin(angle) * radius,
          y: -Math.cos(angle) * radius,
        },
        seatSize,
        location,
        segmentTransform
      );

      if (seatElement.isActive) {
        groupActive.add(seatElement.node);
      } else {
        groupDisabled.add(seatElement.node);
      }
    });
    SeatingUtilsFactory.rotateAroundCenter(groupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(groupDisabled, rotation ?? 0);

    return { groupActive, groupDisabled };
  }

  createSquareTable({
    location,
    transform,
    seatSize,
    segmentTransform,
    hideLocationNames,
    name,
  }: CreateLocationParamsType): {
    groupActive: Konva.Group;
    groupDisabled: Konva.Group;
  } {
    const { x, y, rotation } = transform;

    const groupActive = new Konva.Group({
      x,
      y,
      name: `${name}_active`,
    });

    const groupDisabled = new Konva.Group({
      x,
      y,
      name: `${name}_disabled`,
    });

    // const spacing = ASSIGNED_SEATING.EDITOR.GRID.GRID_WIDTH * 2;
    const tableWidth = seatSize * 4.33;
    const tablePadding = ASSIGNED_SEATING.SQUARE_TABLE.PADDING;
    const midpoint = Math.ceil(location.capacity / 2);
    const width = Math.ceil(location.capacity / 2) * seatSize * 2.5;
    const display = new Konva.Rect({
      x: -width / 2,
      y: -(tableWidth / 2), // half height
      width,
      height: tableWidth,
      fill: ASSIGNED_SEATING.SQUARE_TABLE.STYLE.colorFill,
      stroke: ASSIGNED_SEATING.SQUARE_TABLE.STYLE.colorStroke,
      strokeWidth: ASSIGNED_SEATING.SQUARE_TABLE.STYLE.strokeWidth,
      listening: false,
      name: "display",
    });

    const label = RowFactory.createLabel(location.identifier, {
      width: display.width(),
      height: display.height(),
      fill: ASSIGNED_SEATING.SQUARE_TABLE.STYLE.labelColor,
      fontSize: seatSize * 1.35,
    });

    groupDisabled.add(display);
    if (!hideLocationNames) {
      groupDisabled.add(label);
    }

    // some ratio for setting the spacing amount
    const sizeRatio = seatSize / 3;

    location.seats.forEach((seat, index: number) => {
      const spacing = ASSIGNED_SEATING.EDITOR.GRID.GRID_WIDTH * sizeRatio;

      let seatY = tableWidth / 2 + Number(seatSize) + tablePadding;
      let seatX;

      if (index < midpoint) {
        seatX = spacing * (index + (1 - midpoint) / 2);
        seatY = -seatY;
      } else {
        seatX = spacing * (index - midpoint + (1 - midpoint) / 2);
      }

      const seatElement = this.seatFactory.create(
        seat,
        {
          x: seatX,
          y: seatY,
        },
        seatSize,
        location,
        segmentTransform
      );

      if (seatElement.isActive) {
        groupActive.add(seatElement.node);
      } else {
        groupDisabled.add(seatElement.node);
      }
    });

    SeatingUtilsFactory.rotateAroundCenter(groupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(groupDisabled, rotation ?? 0);

    return { groupActive, groupDisabled };
  }

  createSemiRoundTable({
    location,
    transform,
    seatSize,
    segmentTransform,
    hideLocationNames,
    name,
  }: CreateLocationParamsType): {
    groupActive: Konva.Group;
    groupDisabled: Konva.Group;
  } {
    const { x, y, rotation } = transform;

    const groupActive = new Konva.Group({
      x,
      y,
      name: `${name}_active`,
    });

    const groupDisabled = new Konva.Group({
      x,
      y,
      name: `${name}_disabled`,
    });

    const display = new Konva.Arc({
      x: 0,
      y: 0,
      angle: 180,
      innerRadius: 0,
      outerRadius: seatSize * 2.17,
      rotation: -90,
      width: Math.max(1, Math.floor(location.capacity / 2)) * seatSize * 2.2,
      fill: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.colorFill,
      stroke: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.colorStroke,
      strokeWidth: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.strokeWidth,
      listening: false,
      name: "display",
    });
    const label = RowFactory.createLabel(location.identifier, {
      width: display.width(),
      height: display.height(),
      fill: ASSIGNED_SEATING.CIRCLE_TABLE.STYLE.labelColor,
      fontSize: seatSize * 1.35,
    });

    groupDisabled.add(display);
    if (!hideLocationNames) {
      groupDisabled.add(label);
    }

    location.seats.forEach((seat, index: number) => {
      const tableWidth =
        Math.max(1, Math.floor(location.capacity / 2)) * seatSize * 2.2;
      const tableRadius = tableWidth * 0.5;
      const radius = tableRadius + Number(seatSize) + seatSize / 2; // Radius of the curve around table
      const step = Math.PI / location.capacity;

      const seatElement = this.seatFactory.create(
        seat,
        {
          x: radius * Math.sin(step * (index + 0.5)),
          y: radius * -Math.cos(step * (index + 0.5)),
        },
        seatSize,
        location,
        segmentTransform
      );

      if (seatElement.isActive) {
        groupActive.add(seatElement.node);
      } else {
        groupDisabled.add(seatElement.node);
      }
    });

    SeatingUtilsFactory.rotateAroundCenter(groupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(groupDisabled, rotation ?? 0);

    return { groupActive, groupDisabled };
  }

  create({
    location,
    transform,
    seatSize,
    seatSpacing,
    segmentTransform,
  }: CreateLocationParamsType): {
    groupActive: Konva.Group;
    groupDisabled: Konva.Group;
  } {
    const { x, y, rotation } = transform;
    const defaultSeatSpacing = seatSpacing;

    const { seats, type, spacing: locSeatSpacing, display } = location;
    const groupActive = new Konva.Group({
      x,
      y,
      name: `${type}_active`,
    });

    const groupDisabled = new Konva.Group({
      x,
      y,
      name: `${type}_disabled`,
    });

    // Check if this row has curve data from the display field
    const curvedRowDisplay = display as CurvedRowDisplay;
    const curveEnabled = curvedRowDisplay?.curveEnabled;
    const curvePoints = curvedRowDisplay?.curvePoints;
    const invertSeatOrder = curvedRowDisplay?.invertSeatOrder || false;

    /**
     * Create an array of seat data with positions calculated with proper inversion handling
     */
    const spacing =
      seatSize * 2 + Number(locSeatSpacing || defaultSeatSpacing) * 0.5;

    const seatsArray = seats.map((seat, i) => {
      let seatPosition: { x: number; y: number };

      // Use curved positioning if curve data exists, otherwise straight line
      if (curveEnabled && curvePoints) {
        seatPosition = calculateCurvedSeatPosition({
          seatIndex: i,
          totalSeats: seats.length,
          curvePoints,
          spacing,
          useOptimalSpacing: true, // Use optimal spacing for better distribution
          invertSeatOrder, // Apply inversion during position calculation
        });
      } else {
        // Original straight line positioning with inversion logic
        const adjustedSeatIndex = invertSeatOrder ? seats.length - 1 - i : i;
        seatPosition = {
          x: spacing * (adjustedSeatIndex + (1 - seats.length) / 2),
          y: 0,
        };
      }

      const seatElement = this.seatFactory.create(
        seat,
        seatPosition,
        seatSize,
        location,
        segmentTransform
      );

      return {
        seat,
        seatElement,
      };
    });

    // Process the array and add seat elements to groups
    seatsArray.forEach((seatData) => {
      if (seatData.seatElement.isActive) {
        groupActive.add(seatData.seatElement.node);
      } else {
        groupDisabled.add(seatData.seatElement.node);
      }
    });

    // Build linked list from the array order (maintaining original seat order for navigation)
    seatsArray.forEach((seatData, i) => {
      this.seatingService.seatLinkedList[seatData.seat.id] = {
        prev: i > 0 ? seatsArray[i - 1].seat.id : null,
        next: i < seatsArray.length - 1 ? seatsArray[i + 1].seat.id : null,
      };
    });

    SeatingUtilsFactory.rotateAroundCenter(groupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(groupDisabled, rotation ?? 0);

    return { groupActive, groupDisabled };
  }
}
